/* ============================================
   LeadLienzo — lo que alguien escribió sobre esta oportunidad

   Es `leads.notas`, el mismo campo de siempre, con la superficie que le
   faltaba: arriba del Overview en vez de detrás de una pestaña, y con texto
   con formato en vez de un `<textarea>` detrás de un botón «Editar».

   La pestaña Notas desapareció a propósito. Dos lugares de texto libre en el
   mismo registro es donde se pierden las cosas: nadie sabría cuál tiene el
   contexto de verdad, y a los seis meses la mitad de las oportunidades lo
   tendrían en uno y la mitad en el otro.

   GUARDA SOLO. No hay botón. Es lo que separa un lienzo de un formulario: si
   hay que acordarse de guardar, no se escribe cuando se piensa, se escribe
   cuando hay tiempo — y entonces no se escribe.
   ============================================ */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import RichTextEditor from '@/components/common/RichTextEditor'
import type { Lead } from '@/lib/types'
import { IconCheck, IconMaximize, IconMinimize, IconAlertCircle } from '@/components/icons/Icons'

/** Al rato de dejar de escribir. Corto para que no se pierda nada; largo para
 *  no mandar un PUT por tecla. */
const ESPERA_MS = 1200

interface LeadLienzoProps {
  lead: Lead
  onRefresh: () => void
}

type Estado = 'limpio' | 'escribiendo' | 'guardando' | 'guardado' | 'error'

/** ¿Esto ya es HTML del editor, o texto plano de antes? */
function pareceHtml(valor: string): boolean {
  return /<(p|h[1-6]|ul|ol|li|div|br|strong|em|b|i|u|a|blockquote|table)\b[^>]*>/i.test(valor)
}

function escapar(texto: string): string {
  return texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Las notas escritas antes de esto son texto plano con saltos de línea, y el
 * HTML los colapsa: cargarlas tal cual convertía una página entera en un solo
 * párrafo corrido. Se convierten una vez, al abrir; lo que se guarda después
 * ya es HTML y pasa derecho.
 */
function aHtml(valor: string | null | undefined): string {
  const texto = (valor || '').trim()
  if (!texto) return ''
  if (pareceHtml(texto)) return texto
  return texto
    .split(/\n{2,}/)
    .map(parrafo => `<p>${escapar(parrafo).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

export default function LeadLienzo({ lead, onRefresh }: LeadLienzoProps) {
  const api = useApi()
  const [html, setHtml] = useState(() => aHtml(lead.notas))
  const [estado, setEstado] = useState<Estado>('limpio')
  const [expandido, setExpandido] = useState(false)

  // Refs y no estado: los lee el temporizador y el desmontaje, que corren
  // fuera del render y verían valores viejos.
  const pendienteRef = useRef<string | null>(null)
  const timerRef = useRef<number | null>(null)
  const apiRef = useRef(api)
  const refrescarRef = useRef(onRefresh)
  apiRef.current = api
  refrescarRef.current = onRefresh

  // Al cambiar de oportunidad se recarga el lienzo. Se mira `lead.id` y no
  // `lead.notas`: el refresco del padre devuelve un objeto nuevo con el mismo
  // texto, y reaccionar a eso movería el cursor mientras se escribe.
  useEffect(() => {
    setHtml(aHtml(lead.notas))
    setEstado('limpio')
    pendienteRef.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id])

  const guardar = useCallback(async (valor: string) => {
    pendienteRef.current = null
    setEstado('guardando')
    try {
      await apiRef.current.put(`/api/leads/${lead.id}`, { notas: valor.trim() || null })
      setEstado('guardado')
    } catch (err) {
      console.error('Error guardando el lienzo:', err)
      // Vuelve a la cola: el próximo intento lo reintenta solo, y mientras
      // tanto el aviso en pantalla dice que no está a salvo.
      pendienteRef.current = valor
      setEstado('error')
    }
  }, [lead.id])

  function alEscribir(valor: string) {
    setHtml(valor)
    setEstado('escribiendo')
    pendienteRef.current = valor
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      if (pendienteRef.current != null) guardar(pendienteRef.current)
    }, ESPERA_MS)
  }

  /** Salir del lienzo no espera al temporizador: se guarda ya. */
  function alSalir() {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    if (pendienteRef.current != null) guardar(pendienteRef.current)
  }

  /*
    Al desmontar —cambiar de pestaña, cerrar la ficha— se vacía lo pendiente y
    recién entonces se le avisa al padre. El aviso va acá y no en cada guardado
    porque `onRefresh` vuelve a pedir la lista entera de oportunidades: una vez
    al salir alcanza, y es justo lo que hace falta para que al volver a entrar
    el lienzo no se abra con el texto viejo.
  */
  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      const pendiente = pendienteRef.current
      if (pendiente != null) {
        apiRef.current
          .put(`/api/leads/${lead.id}`, { notas: pendiente.trim() || null })
          .then(() => refrescarRef.current())
          .catch(err => console.error('Error guardando el lienzo al salir:', err))
      } else {
        refrescarRef.current()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id])

  return (
    <section
      className={`ld-lienzo${expandido ? ' ld-lienzo--expandido' : ''}`}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) alSalir() }}
    >
      {/*
        El botón y el estado FLOTAN sobre la esquina del lienzo, no en una
        barra propia: una barra arriba agregaba una franja vacía entre las
        pestañas y el texto, y lo que va ahí no se usa casi nunca. Aparecen al
        pasar el mouse o al escribir.

        El error es la excepción: ese se queda a la vista aunque el mouse no
        esté encima, porque es lo único que el usuario tiene que llegar a ver.
      */}
      <div className={`ld-lienzo__flotante${estado === 'error' ? ' ld-lienzo__flotante--fijo' : ''}`}>
        <span className="ld-lienzo__estado" role="status" aria-live="polite">
          {estado === 'guardando' && 'Guardando...'}
          {estado === 'guardado' && <><IconCheck size={12} /> Guardado</>}
          {estado === 'error' && <><IconAlertCircle size={12} /> Sin guardar</>}
        </span>
        <button
          type="button"
          className="btn-icon-mini ld-lienzo__expandir"
          title={expandido ? 'Contraer' : 'Expandir'}
          aria-label={expandido ? 'Contraer el lienzo' : 'Expandir el lienzo'}
          onClick={() => setExpandido(v => !v)}
        >
          {expandido ? <IconMinimize size={13} /> : <IconMaximize size={13} />}
        </button>
      </div>

      <RichTextEditor
        content={html}
        onChange={alEscribir}
        placeholder="Qué necesita, con quién hablar, qué se prometió, cómo viene la conversación..."
        minHeight={expandido ? 460 : 132}
      />
    </section>
  )
}
